import React, { useState, useEffect, useRef, useContext, useCallback } from "react";
import { UserContext } from "../context/UserContext";

import PlaceIcon from "@mui/icons-material/PlaceOutlined";
import FingerprintIcon from "@mui/icons-material/FingerprintOutlined";
import EventIcon from "@mui/icons-material/EventOutlined";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibraryOutlined";
import DescriptionIcon from "@mui/icons-material/DescriptionOutlined";
import {
  MapContainer,
  TileLayer,
  LayersControl,
  Marker,
  Popup,
  useMap,
  useMapEvents,
} from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-markercluster";
import L from "leaflet";
import "react-leaflet-markercluster/styles";

import "leaflet/dist/leaflet.css";
import "./MapWithSidebar.css";
import { buildLayerWhere } from "../utils/filterUtils";
import { icons, getMercadoMinoristaIcon } from "../utils/icons";
import RightPanel from "./RightPanel";
import Legend from "./Legend";
import ArcGISFeatureLayer from "./ArcGISFeatureLayer";
import ArcGISWMSLayer from "./ArcGISWMSLayer";
import { getDynamicStyle, getMercadoStyle } from "../utils/styleUtils";
import mercados from "../services/mercadosService";
import ChoroplethDptoLayer from "./ChoroplethDptoLayer";
import DepartmentClusterLayer, { CLUSTER_ZOOM } from "./DepartmentClusterLayer";
import { AppConfig } from "../config/appConfig";
import { IconButton, Tooltip } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";   // ↓
import ExpandLessIcon from "@mui/icons-material/ExpandLess";   // ↑
//import { mercadoMinoristaIcons } from "../utils/icons";




// import RightPanelMUI from "./RightPanelMUI";

const { BaseLayer, Overlay } = LayersControl;
/*const BASE_MERCADOS_MINORISTAS =
  process.env.REACT_APP_MERCADOS_MINORISTAS ||
  "http://sigeo.produce.gob.pe:6080/arcgis/rest/services/pndp/mercados_mayoristas/MapServer";*/
/*const BASE_MERCADOS_MAYORISTAS =
  process.env.REACT_APP_MERCADOS_MAYORISTAS ||
  "http://sigeo.produce.gob.pe:6080/arcgis/rest/services/pndp/mercados_minoristas/MapServer";*/
const BASE_PARQUES_INDUSTRIALES = AppConfig.PARQUES_INDUSTRIALES;
const BASE_ZONAS_INDUSTRIALES   = AppConfig.ZONAS_INDUSTRIALES;
const BASE_ATRACTORES           = AppConfig.BASE_ATRACTORES;
const BASE_CORREDORES           = AppConfig.BASE_CORREDORES;




// Sublayers de cada MapServer — usados para renderizar 1 instancia de
// ArcGISFeatureLayer por sublayer (en vez de pasarle un array). Así toggling
// de un checkbox solo afecta a esa instancia: las otras quedan intactas,
// no se re-fetchean, no hay race conditions de unmount/mount entcruzado.
const PARQUES_SUBLAYERS = [0, 1, 2, 3, 4];
const ZONAS_SUBLAYERS = [0, 1, 2];

// =================================================================
// Regla: cada vez que cambia el tab, la cámara se RESETEA a la vista
// por defecto del tab nuevo SIN animación (snap instantáneo).
//   Mercados → Perú entero.
//   Parques  → Perú entero (mismo que Mercados).
//   Zonas    → Lima Metropolitana.
// Tampoco animamos el fitBounds de carga de datos post-tab-change.
// =================================================================
const TAB_DEFAULT_VIEW = {
  0: { center: [-9.19, -75.0152], zoom: 6 },   // Mercados
  2: { center: [-9.19, -75.0152], zoom: 6 },   // Parques
  3: { center: [-12.0464, -77.0428], zoom: 12 }, // Zonas → Lima
};

const applyTabDefaultView = (map, activeTab) => {
  const view = TAB_DEFAULT_VIEW[activeTab] || TAB_DEFAULT_VIEW[0];
  map.setView(view.center, view.zoom, { animate: false });
};

// =================================================================
// Wrappers memoizados — la razón de existir es estabilizar las refs
// de `style`, `popupTemplate`, `onLayerError`. Si se pasaran como
// arrow functions inline desde el JSX, cada render de MapWithOutSidebar
// crearía referencias nuevas → useEffect interno de ArcGISFeatureLayer
// se invalidaría → re-build de la capa → re-fetch al servidor. Eso es
// lo que causa "siempre consulta de nuevo" al togglear.
//
// React.memo + useMemo/useCallback adentro = props estables = el efecto
// interno solo corre cuando cambia algo real (visible, where, etc.).
// =================================================================

const ParqueSublayer = React.memo(function ParqueSublayer({
  id,
  visible,
  where: arcgisFilters,
  onShowDetail,
}) {
  // Construimos el WHERE específico de este sublayer en base a los filtros
  // raw que vienen del FilterPanel. buildLayerWhere ignora los filtros cuyos
  // SQL fields no existen en este `id`, así que cada sublayer solo se afecta
  // por sus filtros aplicables (ej. /4 lotes recibe id_ocupacion/idUso/area,
  // los otros sublayers reciben "1=1").
  //
  // useMemo deps con primitivos (whereSql, text) — para que si la ref de
  // `arcgisFilters` cambia pero el SQL computado queda igual (caso común:
  // aplicar un filtro que afecta a /4 no debe rebuildar /2), el `where`
  // mantenga la misma ref y el effect interno no rebuildee.
  const filters =
    typeof arcgisFilters === "object" && arcgisFilters ? arcgisFilters : {};
  const whereSql = buildLayerWhere("parquesIndustriales", id, filters);
  const text = filters.__text || "";
  const where = React.useMemo(
    () => ({ __rawWhere: whereSql, __text: text }),
    [whereSql, text]
  );

  const style = React.useMemo(
    () => (feature) =>
      getDynamicStyle(feature, [id], "PARQUES", {
        weight: 0.5,
        opacity: 0.5,
        fillOpacity: 1,
      }),
    [id]
  );

  // popupTemplate solo se consume cuando activeTab no es 2/3 — para tab
  // parques los popups concretos los rendea ArcGISFeatureLayer
  // (popupParqIndustrialesDetalle, popupParLotIndDetalle, etc). Aún así
  // lo memoizamos para no contaminar los deps del effect interno.
  const popupTemplate = React.useMemo(
    () => (feature, lid) => {
      const props = feature.properties || {};
      if (lid === 3) {
        return `
          <div style="max-width: auto">
            <h4 style="margin: 0 0 8px 0; color: #333;"> ID: Mz: ${props.mz || props.valor_descripcion} ${(props.lote ? 'Lote:' + props.lote : '') || ''} ${" - Lote Industrial"}</h4>
            <hr style="margin: 8px 0;">
          </div>
        `;
      }
      return `
        <div style="max-width: auto">
          <h4 style="margin: 0 0 8px 0; color: #333;"> ID: ${props.name || ''} - Parque Industrial</h4>
        </div>
      `;
    },
    []
  );

  const onLayerError = React.useCallback(
    (error) => {
      console.error(
        `Error al cargar capa de Parques Industriales (sublayer ${id}):`,
        error
      );
    },
    [id]
  );

  return (
    <ArcGISFeatureLayer
      visible={visible}
      // Sublayers con popup summary + modal: ZED (1), parques industriales
      // (2), zonificación (3), unidades inmobiliarias/lotes (4).
      onShowDetail={[1, 2, 3, 4].includes(id) ? onShowDetail : undefined}
      url={BASE_PARQUES_INDUSTRIALES}
      layerId={id}
      where={where}
      activeTab={2}
      layerType="parquesWFS"
      style={style}
      popupTemplate={popupTemplate}
      onLayerError={onLayerError}
    />
  );
});

// =================================================================
// MarkerItem — un wrapper React.memo'd alrededor de <Marker>.
// Cada vez que MapWithOutSidebar re-renderea (por ejemplo: cuando
// parques/zonas terminan de cargar y Map.js setea parquesCount/zonasCount),
// la lista de Markers se vuelve a evaluar en JSX. Sin memo, cada <Marker>
// recibía nuevos refs para `position`, `icon` y `eventHandlers` — y
// react-leaflet en su updateMarker invocaba `setLatLng`/`setIcon` aunque
// nada cambió visualmente. Eso reiniciaba la animación CSS `pxPulse` del
// .px-marker-ring, sincronizando todos los rings al frame 0 → el blink.
//
// Con React.memo + comparator estricto, mientras el item y el onClick sean
// la misma ref, el inner Marker NO re-renderiza, react-leaflet no toca el
// DOM y la animación corre suave.
// =================================================================
const MarkerItem = React.memo(
  function MarkerItem({ item, icon, onClick }) {
    const handleClick = React.useCallback(() => onClick(item), [item, onClick]);
    const handlers = React.useMemo(
      () => ({ click: handleClick }),
      [handleClick]
    );
    const position = React.useMemo(
      () => [item.latitud, item.longitud],
      [item.latitud, item.longitud]
    );
    return <Marker position={position} icon={icon} eventHandlers={handlers} />;
  },
  (prev, next) =>
    prev.item === next.item &&
    prev.icon === next.icon &&
    prev.onClick === next.onClick
);

const ZonaSublayer = React.memo(function ZonaSublayer({
  id,
  visible,
  where: arcgisFilters,
  onShowDetail,
}) {
  const filters =
    typeof arcgisFilters === "object" && arcgisFilters ? arcgisFilters : {};
  const whereSql = buildLayerWhere("zonasIndustriales", id, filters);
  const text = filters.__text || "";
  const where = React.useMemo(
    () => ({ __rawWhere: whereSql, __text: text }),
    [whereSql, text]
  );

  const style = React.useMemo(
    () => (feature) =>
      getDynamicStyle(feature, [id], "ZONAS", {
        weight: 2,
        opacity: 0.4,
        fillOpacity: 0.4,
      }),
    [id]
  );

  const popupTemplate = React.useMemo(
    () => (feature, lid) => {
      const props = feature.properties || {};
      if ([1, 2].includes(lid)) {
        return `
          <div style="max-width: 300px;">
            <h4 style="margin: 0 0 8px 0; color: #333;">${
              "ID: " +
              (props.id_zonificacion == null
                ? props.valor_descripcion
                : props.valor_descripcion + " - Zonificación" || "Sin valor")
            }</h4>
            <hr style="margin: 8px 0;">
          </div>
        `;
      }
      return `
        <div class="card-table">
          <h4 class="card-title">
            ID: ${props.valor_descripcion?.split(".")[0]?.trim() || "Sin valor"} - Zona Industrial
          </h4>
          <hr class="card-divider">
          <table class="info-table">
            <tbody>
              ${props.uso_decripcion ? `<tr><td class="label">Uso</td><td>${props.uso_decripcion}</td></tr>` : ""}
              ${props.ruc ? `<tr><td class="label">RUC</td><td>${props.ruc}</td></tr>` : ""}
              ${props.identificacion_descripcion ? `<tr><td class="label">Identificación</td><td>${props.identificacion_descripcion}</td></tr>` : ""}
              ${props.edificacion_descripcion ? `<tr><td class="label">Tipo de Edificación</td><td>${props.edificacion_descripcion}</td></tr>` : ""}
              ${props.rubro_descripcion ? `<tr><td class="label">Rubro</td><td>${props.rubro_descripcion}</td></tr>` : ""}
              ${props.construccion_descripcion ? `<tr><td class="label">Construcción</td><td>${props.construccion_descripcion}</td></tr>` : ""}
              ${props.ocupacion_descripcion ? `<tr><td class="label">Ocupación</td><td>${props.ocupacion_descripcion}</td></tr>` : ""}
              ${props.area ? `<tr><td class="label">Área Total</td><td>${Number(props.area).toLocaleString("es-PE", { maximumFractionDigits: 2 })} m²</td></tr>` : ""}
            </tbody>
          </table>
        </div>
      `;
    },
    []
  );

  const onLayerError = React.useCallback(
    (error) => {
      console.error(
        `Error al cargar capa de Zonas Industriales (sublayer ${id}):`,
        error
      );
    },
    [id]
  );

  return (
    <ArcGISFeatureLayer
      visible={visible}
      // Solo el sublayer principal (Capa Zonas Industriales, id=2) tiene el
      // popup summary + modal de detalle. Los otros (0=Unidades Inmobiliarias,
      // 1=Tipo Zonificación) son auxiliares y no necesitan modal.
      onShowDetail={id === 2 ? onShowDetail : undefined}
      url={BASE_ZONAS_INDUSTRIALES}
      layerId={id}
      where={where}
      activeTab={3}
      style={style}
      popupTemplate={popupTemplate}
      onLayerError={onLayerError}
    />
  );
});

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const MapRefController = ({ onReady }) => {
  const map = useMap();
  useEffect(() => {
    if (map) onReady(map);
  }, [map]);
  return null;
};

const MapController = ({ centroide }) => {
  const map = useMap();
  useEffect(() => {
    if (centroide && centroide.centroide && centroide.centroide.length === 2) {
      map.setView(
        [centroide.centroide[0], centroide.centroide[1]],
        centroide.zoom || 10
      );
    }
  }, [centroide, map]);
  return null;
};

const MapClickHandler = ({ onMapClick }) => {
  useMapEvents({
    click: onMapClick,
  });
  return null;
};

const MapWithOutSidebar = ({
  filteredData,
  activeTab,
  arcgisWhereClause = {},
  centroide = [-9.19, -75.0152],
  layerId = [],
  onMapClick,
  selectedFilters,
  isSearchMode,
  theme = "light",
  selectedPairs,
  // Callback que Map.js usa para re-abrir el FilterPanel (state es de Map.js)
  // cuando se cierra el modal de detalle.
  onDetailModalClose,
  togglePair,
}) => {
  const { authenticated } = useContext(UserContext);

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [defaultData, setDefaultData] = useState([]);
  const [mapRef, setMapRef] = useState(null);
  const [showLegend, setShowLegend] = useState(true);
  const [mapZoom, setMapZoom] = useState(6);
  const handleZoomChange = useCallback((z) => setMapZoom(z), []);

  // Conteos para mostrar como badges en el info window
  const [popupCounts, setPopupCounts] = useState({
    imagenes: null,
    pdfs: null,
  });

  // Position del Popup memoizada — sin esto el useEffect interno del Popup
  // de react-leaflet (que tiene `position` en sus deps) corría `removeLayer`
  // + `openOn(map)` en cada render: cuando setPopupCounts se disparaba al
  // resolver getMercadoAdminImagenes/Pdfs, el popup se desmontaba y
  // remontaba — eso era el blink al hacer click en un marker.
  const popupPosition = React.useMemo(
    () =>
      selectedItem &&
      selectedItem.latitud != null &&
      selectedItem.longitud != null
        ? [selectedItem.latitud, selectedItem.longitud]
        : null,
    [selectedItem?.latitud, selectedItem?.longitud]
  );

  const popupOffset = React.useMemo(() => [0, -24], []);

  const handlePopupRemove = useCallback(() => setSelectedItem(null), []);

  const popupEventHandlers = React.useMemo(
    () => ({ remove: handlePopupRemove }),
    [handlePopupRemove]
  );

  useEffect(() => {
    if (!selectedItem?.id || activeTab !== 0) {
      setPopupCounts({ imagenes: null, pdfs: null });
      return;
    }
    const id = selectedItem.id;
    setPopupCounts({ imagenes: null, pdfs: null });
    let cancelled = false;

    mercados
      .getMercadoAdminImagenes(id)
      .then((resp) => {
        if (cancelled) return;
        const items = Array.isArray(resp) ? resp : resp?.items ?? [];
        setPopupCounts((p) => ({ ...p, imagenes: items.length }));
      })
      .catch(() => {});

    if (authenticated) {
      mercados
        .getMercadoAdminPdfs(id)
        .then((resp) => {
          if (cancelled) return;
          const items = Array.isArray(resp) ? resp : resp?.items ?? [];
          setPopupCounts((p) => ({ ...p, pdfs: items.length }));
        })
        .catch(() => {});
    }

    return () => {
      cancelled = true;
    };
  }, [selectedItem, activeTab, authenticated]);

  // Al cambiar filtros: cerrar modal y limpiar selectedItem. Como el popup
  // ahora se renderiza condicional a `selectedItem`, ponerlo null lo
  // desmonta automáticamente — no se necesita closePopup() imperativo.
  useEffect(() => {
    setSelectedItem(null);
    setSidebarOpen(false);
  }, [filteredData, arcgisWhereClause]);

  // Cuando el modal de detalle está abierto, marcamos document.body con
  // `has-detail-open` para que el CSS oculte FilterPanel y Legend.
  // Los paneles transicionan a su estado colapsado (usando los mismos
  // transforms que .is-collapsed) y reaparecen cuando el modal se cierra.
  useEffect(() => {
    const isDetailVisible = sidebarOpen && !!selectedItem;
    if (isDetailVisible) {
      document.body.classList.add("has-detail-open");
    } else {
      document.body.classList.remove("has-detail-open");
    }
    return () => {
      document.body.classList.remove("has-detail-open");
    };
  }, [sidebarOpen, selectedItem]);

  // Trackea el activeTab del render anterior para detectar cambio de tab.
  const prevActiveTabRef = useRef(activeTab);

  // Timestamp del último cambio de tab (incluye mount inicial). Durante una
  // ventana de gracia después de un cambio, ignoramos cualquier zoom/flyTo
  // disparado por `filteredData` cambiando. Esto previene que FilterPanel,
  // que dispara handleApplyFilters varias veces mientras se asientan
  // selectedPairs/filters/handleApplyFilters al inicializar el tab nuevo,
  // pise la vista por defecto que ya aplicamos en applyTabDefaultView.
  // Sólo el primer evento de animación quedaba bloqueado antes; ahora
  // toda la ráfaga inicial queda muteada.
  const tabChangeAtRef = useRef(Date.now());
  const TAB_CHANGE_GRACE_MS = 3000;

  useEffect(() => {
    if (!mapRef) return;

    const tabChanged = prevActiveTabRef.current !== activeTab;
    prevActiveTabRef.current = activeTab;

    if (tabChanged) {
      // Reseteamos selection y popups, después aplicamos la vista por
      // defecto del tab nuevo según TAB_DEFAULT_VIEW.
      setSelectedItem(null);
      mapRef.closePopup();
      mapRef.eachLayer((layer) => layer.closePopup?.());
      applyTabDefaultView(mapRef, activeTab);
      tabChangeAtRef.current = Date.now();
      return;
    }

    // En el mismo tab — filteredData puede cambiar (filtros, búsqueda).
    // Solo limpiamos popups si vuelve a null.
    if (filteredData === null) {
      setSelectedItem(null);
      mapRef.closePopup();
      mapRef.eachLayer((layer) => layer.closePopup?.());
      return;
    }

    if (!filteredData || filteredData.length === 0) return;

    // Ventana de gracia post-tab-change: mientras estemos adentro,
    // cualquier filteredData que llegue es FilterPanel re-applicándose por
    // el reset de filtros/selectedPairs — NO una acción del usuario.
    // No animamos ni movemos la cámara.
    const elapsedSinceTabChange = Date.now() - tabChangeAtRef.current;
    if (elapsedSinceTabChange < TAB_CHANGE_GRACE_MS) {
      return;
    }

    if (filteredData.length === 1) {
      const first = filteredData[0];
      if (!first.latitud || !first.longitud) return;

      // Centramos el conjunto marker+popup en el ÁREA VISIBLE del mapa
      // (compensando el chrome superior). Mismo cálculo que handleMarkerClick.
      const targetZoom = 15;
      const CHROME_TOP_PX = 104;
      const COMBO_HALF_PX = 116;
      const offsetPx = CHROME_TOP_PX / 2 + COMBO_HALF_PX; // ~168
      try {
        const markerPx = mapRef.project([first.latitud, first.longitud], targetZoom);
        const offsetCenterPx = markerPx.subtract([0, offsetPx]);
        const offsetCenter = mapRef.unproject(offsetCenterPx, targetZoom);
        mapRef.flyTo(offsetCenter, targetZoom, { duration: 1.6 });
      } catch (e) {
        mapRef.flyTo([first.latitud, first.longitud], targetZoom, { duration: 1.6 });
      }

      // Auto-abrir el popup standalone vía estado React.
      setSelectedItem(first);
    } else {
      // Salvaguarda extra: si filteredData equivale al total (sin filtros)
      // tampoco animamos, aunque ya pasamos el guard de "primera arrival".
      const isUnfiltered =
        Array.isArray(defaultData) &&
        defaultData.length > 0 &&
        filteredData.length === defaultData.length;
      if (isUnfiltered) return;

      const points = filteredData
        .filter(d => d.latitud && d.longitud)
        .map(d => [d.latitud, d.longitud]);

      if (points.length > 0) {
        mapRef.fitBounds(points, { padding: [50, 50], maxZoom: 15 });
      }
    }

  }, [filteredData, activeTab, mapRef, defaultData]);

  useEffect(() => {
    const loadDefaultData = async () => {
      try {
        let dataModule;

        switch (activeTab) {
          case 0: // Minoristas
            dataModule = await mercados.getMercadosMinoristas();//await import("../data/intervenciones_real_minorista"); //
            break;
          case 1: // Mayoristas
            dataModule = await import("../data/intervenciones_real_mayorista");
            break;
          case 2: // Parques industriales
            dataModule = [];
            break;
          case 3: // Zonas industriales
            dataModule = [];
            break;
          default:
            dataModule = await import("../data/intervenciones_real_minorista");
            break;
        }

        const data = dataModule.default || dataModule;
        setDefaultData(data);
        // Close RightPanel when tab changes
        setSelectedItem(null);
        setSidebarOpen(false);
      } catch (error) {
        console.error("Error al cargar datos por defecto:", error);
      }
    };

    loadDefaultData();
    // if (centroide) {
    //       map.panTo(centroide); // or map.setView(newCenter, map.getZoom());
    //     }
  }, [activeTab, centroide]);


  // Handler para "Ver detalle" del popup de Parques Industriales /
  // Zonificación / Lotes. El item ya viene con `kind`, `id` y demás campos
  // que RightPanel necesita para despachar al componente correcto
  // (DetalleParqueIndustrial / DetalleZonificacionIndustrial / DetalleLoteIndustrial).
  //
  // useCallback estable para no invalidar el efecto interno de
  // ArcGISFeatureLayer (que tiene `onShowDetail` en sus deps) en cada
  // re-render — evita rebuild/re-fetch innecesario de las capas.
  const handleParqueShowDetail = useCallback((item) => {
    setSelectedItem(item);
    setSidebarOpen(true);
  }, []);

  // useCallback estable — sin esto, cada render de MapWithOutSidebar creaba
  // una función nueva, que se pasaba a cada <Marker> como prop, y rompía la
  // memoización del MarkerItem (React.memo). En consecuencia los markers
  // re-renderizaban y react-leaflet llamaba `marker.setLatLng/setIcon` aunque
  // no había cambiado nada visible — la animación CSS `pxPulse` del ring
  // entonces se sincronizaba en todos los markers a la vez (= el "blink").
  const handleMarkerClick = useCallback((item) => {
    // Determinamos el estilo (colores) basado en la pestaña activa
    let style = { backgroundColor: "#808080", color: "#fff" };

    if (activeTab === 0) {
      style = getMercadoStyle(item.id_estado_intervencion, item.id_modalidad);
    } else if (activeTab === 1) {
      style = { backgroundColor: "#2e7d32", color: "#fff" };
    } else if (activeTab === 2) {
      style = { backgroundColor: "#5e014eff", color: "#fff" };
    } else if (activeTab === 3) {
      style = { backgroundColor: "#a12323ff", color: "#fff" };
    }

    // El click en el marker SOLO abre el infowindow (popup).
    // El modal de detalle se abre exclusivamente cuando el usuario
    // clickea "Ver detalle" dentro del popup (ver el CTA más abajo).
    setSelectedItem({ ...item, headerStyle: style });

    // Centramos marker+popup en el área visible SIN cambiar el zoom.
    // Esto evita el "runaway zoom" cuando el usuario clickea marcadores
    // consecutivamente. El autoPan del popup ya está deshabilitado, así
    // que nuestro panTo es el único actor en escena.
    if (mapRef && item.latitud != null && item.longitud != null) {
      const currentZoom = mapRef.getZoom();
      const CHROME_TOP_PX = 104;
      const COMBO_HALF_PX = 116;
      const offsetPx = CHROME_TOP_PX / 2 + COMBO_HALF_PX; // ~168
      try {
        const markerPx = mapRef.project([item.latitud, item.longitud], currentZoom);
        const offsetCenterPx = markerPx.subtract([0, offsetPx]);
        const offsetCenter = mapRef.unproject(offsetCenterPx, currentZoom);
        mapRef.panTo(offsetCenter, { animate: true, duration: 0.6 });
      } catch (e) {
        mapRef.panTo([item.latitud, item.longitud], { animate: true, duration: 0.6 });
      }
    }
  }, [activeTab, mapRef]);
  /*
 const handleMarkerClick = async (item) => {
  if (item.id) {
    try {
      const marketDetails = await mercados.getMercadosDetalle(item.id);
      console.log("Detalles del marcador seleccionado:", marketDetails);
      setSelectedItem(marketDetails);
      
    } catch (error) {
      console.error("Error al obtener los detalles del mercado:", error);
    }
  } 
  
  setSidebarOpen(true); // Abrimos el sidebar
};
*/
  return (
    <div className={`map-layout ${showLegend ? "has-legend-open" : ""}`}>
      {/* Mapa */}
      <div className="map-container">
        <MapContainer
          center={[-9.19, -75.0152]}
          zoom={6}
          style={{ height: "100%", width: "100%" }}
        >
          <MapRefController onReady={setMapRef} />

          {/* LayersControl con todas las capas originales + 2 nuevas (Mapa Claro / Mapa Oscuro).
              key={theme} fuerza remount al togglear tema, lo cual reactiva la BaseLayer
              marcada como `checked` según el tema. */}
          <LayersControl key={`lc-${theme}`} position="topright">
            {/* Capas Light/Dark sincronizadas con el toggle del header */}
            <BaseLayer checked={theme === "light"} name="Mapa Claro">
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
                url={AppConfig.TILE_CARTO_LIGHT}
              />
            </BaseLayer>
            <BaseLayer checked={theme === "dark"} name="Mapa Oscuro">
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
                url={AppConfig.TILE_CARTO_DARK}
              />
            </BaseLayer>

            {/* Capas base originales conservadas */}
            <BaseLayer name="OSM Estándar">
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
                url={AppConfig.TILE_OSM}
              />
            </BaseLayer>
            <BaseLayer name="OSM Topográfico">
              <TileLayer
                attribution='&copy; OpenTopoMap'
                url={AppConfig.TILE_TOPO}
              />
            </BaseLayer>
            <BaseLayer name="ESRI Satelital">
              <TileLayer
                attribution='Tiles &copy; Esri'
                url={AppConfig.TILE_ESRI_SAT}
              />
            </BaseLayer>
            {/*<Overlay name="ZONA CHANCAY">
                <ArcGISWMSLayer
                  url={BASE_CHANCAY}
                  layers="0"
                  format="image/png"
                  transparent={true}
                  version="1.1.1"
                />
              </Overlay>*/}
            {/*activeTab == 2 && (
              <Overlay checked name="Límites (WMS ArcGIS)">
                <ArcGISWMSLayer
                  url={BASE_DEPARTAMENTO}
                  layers="0"
                  format="image/png"
                  transparent={true}
                  version="1.1.1"
                />
              </Overlay>
            )*/}
            <Overlay name="ZONAS ATRACTORAS">
              <ArcGISWMSLayer
                url={BASE_ATRACTORES}
                layers="0"
                format="image/png"
                transparent={true}
                version="1.1.1"
              />
            </Overlay>
            <Overlay name="CL-ESTRUCTURANTE">
              <ArcGISWMSLayer
                url={BASE_CORREDORES}
                layers="3"
                format="image/png"
                transparent={true}
                version="1.1.1"
              />
            </Overlay>
            <Overlay name="CL-CONSOLIDADO">
              <ArcGISWMSLayer
                url={BASE_CORREDORES}
                layers="2"
                format="image/png"
                transparent={true}
                version="1.1.1"
              />
            </Overlay>
            <Overlay name="CL-EN CONSOLIDACIÓN">
              <ArcGISWMSLayer
                url={BASE_CORREDORES}
                layers="1"
                format="image/png"
                transparent={true}
                version="1.1.1"
              />
            </Overlay>
            <Overlay name="CL-POTENCIAL">
              <ArcGISWMSLayer
                url={BASE_CORREDORES}
                layers="0"
                format="image/png"
                transparent={true}
                version="1.1.1"
              />
            </Overlay>
            { }

          </LayersControl>

          {/* Pre-fetch: TODOS los sublayers de parques y zonas se montan
              al iniciar la app (incluso si estás en otro tab). Cada uno
              hace su fetch inicial en background; el pane queda oculto
              hasta que el usuario lo necesite. Cambiar de tab = puro
              cambio de CSS, sin red.
              `visible` = (activeTab del tab) AND (checkbox del sublayer). */}
          {PARQUES_SUBLAYERS.map((id) => (
            <ParqueSublayer
              key={`parques-${id}`}
              id={id}
              visible={activeTab === 2 && layerId.includes(id)}
              where={arcgisWhereClause}
              onShowDetail={handleParqueShowDetail}
            />
          ))}

          {ZONAS_SUBLAYERS.map((id) => (
            <ZonaSublayer
              key={`zonas-${id}`}
              id={id}
              visible={activeTab === 3 && layerId.includes(id)}
              where={arcgisWhereClause}
              onShowDetail={handleParqueShowDetail}
            />
          ))}
          <MapController centroide={centroide} />
          <MapClickHandler onMapClick={onMapClick} />
          {activeTab === 0 && (
            <ChoroplethDptoLayer points={Array.isArray(filteredData) ? filteredData : defaultData} />
          )}
          {activeTab === 0 && (
            <DepartmentClusterLayer
              points={Array.isArray(filteredData) ? filteredData : defaultData}
              zoom={mapZoom}
              onZoomChange={handleZoomChange}
            />
          )}
          {(() => {
            // Markers individuales solo cuando hay suficiente zoom
            if (activeTab === 0 && mapZoom < CLUSTER_ZOOM) return null;
            const items = Array.isArray(filteredData) ? filteredData : defaultData;
            const markers = items.map((item, idx) => {
              // Icon ya viene cacheado por (estado, modalidad) desde icons.js,
              // así que misma combinación = misma ref entre renders.
              const icon =
                activeTab === 0
                  ? getMercadoMinoristaIcon(
                      item.id_estado_intervencion,
                      item.id_modalidad
                    )
                  : icons[item.tipo] || icons.default;
              return (
                <MarkerItem
                  // Key estable — Math.random() generaba una key nueva en
                  // cada render para items sin id, forzando remount.
                  key={
                    item.id ??
                    item.cui ??
                    `${item.latitud}-${item.longitud}-${idx}`
                  }
                  item={item}
                  icon={icon}
                  onClick={handleMarkerClick}
                />
              );
            });

            return markers;
          })()}

          {/* Popup standalone — su ciclo de vida lo controla `selectedItem`.
              No está bound a un <Marker>, así que no se cierra cuando ese
              re-renderiza ni cuando el modal se cierra. Solo desaparece
              cuando el usuario clickea su X (onClose) o cuando los filtros
              cambian y limpian selectedItem. */}
          {selectedItem &&
            selectedItem.latitud != null &&
            selectedItem.longitud != null &&
            (() => {
              // Calcula etapa y color de acento desde el estado/modalidad,
              // solo aplicable para mercados (tab 0). Mismo mapping que usa
              // el modal de detalle para mantener consistencia visual.
              const E = Number(selectedItem.id_estado_intervencion);
              const M = Number(selectedItem.id_modalidad);
              let etapaLabel = null;
              if (activeTab === 0) {
                if (E === 1) etapaLabel = "Obra ejecutada";
                else if (E === 2) etapaLabel = "Obra en ejecución";
                else if (E === 3 || E === 4 || (E === 7 && M === 14))
                  etapaLabel = "Expediente técnico";
                else if (E === 6 || (E === 7 && M === 15))
                  etapaLabel = "Preinversión";
              }
              const accent =
                activeTab === 0 && E
                  ? getMercadoStyle(E, M).backgroundColor
                  : "#475569";

              return (
                <Popup
                  position={popupPosition}
                  className="mki-popup-wrap"
                  closeButton={true}
                  closeOnClick={false}
                  autoClose={false}
                  autoPan={false}
                  offset={popupOffset}
                  eventHandlers={popupEventHandlers}
                >
                  <div className="mki-popup" style={{ "--c": accent }}>
                    {etapaLabel && (
                      <span className="mki-popup-pill">{etapaLabel}</span>
                    )}
                    <h3 className="mki-popup-title">{selectedItem.nombre}</h3>

                    <div className="mki-popup-meta-list">
                      {(selectedItem.departamento ||
                        selectedItem.provincia ||
                        selectedItem.distrito) && (
                        <div className="mki-popup-meta-row">
                          <PlaceIcon style={{ fontSize: 14 }} />
                          <span>
                            {[
                              selectedItem.departamento,
                              selectedItem.provincia,
                              selectedItem.distrito,
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </span>
                        </div>
                      )}
                      {selectedItem.cui && (
                        <div className="mki-popup-meta-row">
                          <FingerprintIcon style={{ fontSize: 14 }} />
                          <span>
                            CUI <strong>{selectedItem.cui}</strong>
                          </span>
                        </div>
                      )}
                      {selectedItem.fechaActualizacion && (
                        <div className="mki-popup-meta-row">
                          <EventIcon style={{ fontSize: 14 }} />
                          <span>
                            Actualizado{" "}
                            <strong>{selectedItem.fechaActualizacion}</strong>
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Stats — counts de imágenes y documentos.
                        Cuando alguno está en null estamos esperando la API:
                        mostramos un skeleton shimmer en lugar de ocultar el
                        chip, así el usuario sabe que hay info en camino. */}
                    {activeTab === 0 && (
                      <div className="mki-popup-stats">
                        <span
                          className={
                            popupCounts.imagenes === null
                              ? "mki-popup-stat mki-popup-stat--loading"
                              : "mki-popup-stat"
                          }
                          title={
                            popupCounts.imagenes === null
                              ? "Cargando imágenes…"
                              : `${popupCounts.imagenes} imágenes`
                          }
                        >
                          <PhotoLibraryIcon style={{ fontSize: 14 }} />
                          {popupCounts.imagenes === null ? (
                            <>
                              <span className="mki-popup-skel mki-popup-skel--num" />
                              <span className="mki-popup-skel mki-popup-skel--word" />
                            </>
                          ) : (
                            <>
                              <strong>{popupCounts.imagenes}</strong>
                              <span className="mki-popup-stat-label">
                                {popupCounts.imagenes === 1
                                  ? "imagen"
                                  : "imágenes"}
                              </span>
                            </>
                          )}
                        </span>
                        {/* Solo mostramos el chip de PDFs si el usuario está
                            autenticado — para anónimos, la API no se
                            consulta y el skeleton se quedaría infinito. */}
                        {authenticated && (
                          <span
                            className={
                              popupCounts.pdfs === null
                                ? "mki-popup-stat mki-popup-stat--loading"
                                : "mki-popup-stat"
                            }
                            title={
                              popupCounts.pdfs === null
                                ? "Cargando documentos…"
                                : `${popupCounts.pdfs} documentos`
                            }
                          >
                            <DescriptionIcon style={{ fontSize: 14 }} />
                            {popupCounts.pdfs === null ? (
                              <>
                                <span className="mki-popup-skel mki-popup-skel--num" />
                                <span className="mki-popup-skel mki-popup-skel--word" />
                              </>
                            ) : (
                              <>
                                <strong>{popupCounts.pdfs}</strong>
                                <span className="mki-popup-stat-label">
                                  {popupCounts.pdfs === 1
                                    ? "documento"
                                    : "documentos"}
                                </span>
                              </>
                            )}
                          </span>
                        )}
                      </div>
                    )}

                    <button
                      type="button"
                      className="mki-popup-cta"
                      onClick={(e) => {
                        e.stopPropagation();
                        // Único punto donde se abre el modal de detalle.
                        // El marker click sólo muestra este popup; el modal
                        // requiere acción explícita del usuario aquí.
                        setSidebarOpen(true);
                      }}
                    >
                      Ver detalle <span aria-hidden>→</span>
                    </button>
                  </div>
                </Popup>
              );
            })()}
        </MapContainer>
      </div>
      <RightPanel
        isOpen={sidebarOpen}
        onClose={() => {
          // Al cerrar el modal de detalle, garantizamos que los paneles
          // laterales vuelvan a estar abiertos — aunque el usuario los
          // hubiera colapsado antes de abrir el modal. UX: el detalle se
          // cierra y reaparece todo el chrome de filtros/leyenda.
          setSidebarOpen(false);
          setShowLegend(true);
          onDetailModalClose?.();
        }}
        selectedItem={selectedItem}
        filteredData={filteredData}
        selectedFilters={selectedFilters}
        isSearchMode={isSearchMode}
      />
      {/* Leyenda — siempre se renderiza; el handle interno la colapsa.
          Cuando está abierta, ocultamos el control de capas de leaflet
          (vía la clase `has-legend-open` en .map-layout) y al cerrarse
          el icono de capas reaparece con popup animation. */}
      <Legend
        activeTab={activeTab}
        selectedPairs={selectedPairs}
        togglePair={togglePair}
        isOpen={showLegend}
        onToggle={() => setShowLegend((prev) => !prev)}
      />

    </div>
  );
};

export default MapWithOutSidebar;
